import { useEffect, useRef, useState } from 'react'
import { usePostHog } from '@posthog/react'
import { Routes, Route, NavLink, Navigate, useLocation, useSearchParams } from 'react-router-dom'
import {
  extractBuildResult,
  fetchConversation,
  fetchConversationMessages,
  fetchHealth,
  generateAssembledImage,
  lookupAssembledImage,
  saveConversationBuild,
  sendChatTurnStream,
  type BuildProgressSnapshot,
  type WatchBuildResult,
  type WatchPart,
} from './api/client'
import {
  captureAssembledImageCacheHit,
  captureAssembledImageCompleted,
  captureAssembledImageFailed,
  captureAssembledImageRequested,
  captureBuyFullBuildClicked,
  captureChatMessageSent,
  captureChatTurnCompleted,
  captureChatTurnFailed,
  capturePurchaseLinkClicked,
  captureStudioBuildReady,
  captureStudioBuildRestored,
  captureStudioBuildLinkCopied,
  captureStudioBuildViewed,
  captureStudioNewBuildStarted,
  captureStudioPhaseCompleted,
  captureStudioRefinementStarted,
  captureStudioRetryRequested,
  captureStudioStarted,
  captureStudioViewChanged,
  captureSuggestionClicked,
  registerConversation,
  type SuggestionSource,
} from './analytics/events'
import { Sentry } from './sentry'
import { applyCaseVariantSelection } from './lib/partVariants'
import {
  clearStoredConversationId,
  CONVERSATION_QUERY_PARAM,
  conversationPermalink,
  copyTextToClipboard,
  parseConversationId,
  persistConversationId,
  resolveResumeConversationId,
  WATCH_BUILDER_PATH,
} from './lib/studioSession'
import './App.css'
import { ChatPanel, messagesFromConversation, type UiChatMessage } from './components/ChatPanel'
import { RetrievalLab } from './components/RetrievalLab'
import { AgentObserver } from './components/AgentObserver'
import { AtelierShell, type AtelierTab } from './components/AtelierShell'
import { WatchStage } from './components/WatchStage'
import { TechnicalLoupe } from './components/TechnicalLoupe'
import { HomePage } from './components/HomePage'
import { SiteLayout } from './components/SiteLayout'
import { CatalogAdminLayout } from './components/CatalogAdminLayout'
import { CatalogAdmin } from './components/CatalogAdmin'
import { CatalogPartEditor } from './components/CatalogPartEditor'
import {
  buildIdentity,
  deriveStudioState,
  isCompleteCompatibleBuild,
  type ImageLookupState,
  type StudioPhase,
} from './studioState'

const milestoneQuotes = [
  {
    occasion: 'Graduation',
    quote: 'A first serious watch for the first day the world calls you by your title.',
  },
  {
    occasion: 'Wedding',
    quote: 'Something quiet, precise, and worthy of the vows it will witness.',
  },
  {
    occasion: 'Promotion',
    quote: 'Mark the room you earned — a watch that feels made for one.',
  },
  {
    occasion: 'New Chapter',
    quote: 'A reminder that time can be lived with better intentions.',
  },
]

const initialMessages: UiChatMessage[] = [
  {
    id: 'welcome',
    role: 'assistant',
    content:
      'Tell me the milestone, the style you imagine, and any size or budget constraints. I will translate that into compatible watch parts.',
  },
]

function computeUpdatedParts(
  previous: WatchBuildResult | null,
  current: WatchBuildResult | null,
): Set<string> {
  if (!previous || !current) return new Set()
  const updated = new Set<string>()
  for (const [partType, part] of Object.entries(current.build.parts)) {
    const prev = previous.build.parts[partType]
    const previousVariant = prev?.metadata?.selected_variant_id ?? prev?.metadata?.variant_id
    const currentVariant = part.metadata?.selected_variant_id ?? part.metadata?.variant_id
    if (
      !prev ||
      prev.id !== part.id ||
      String(previousVariant ?? '') !== String(currentVariant ?? '')
    ) {
      updated.add(partType)
    }
  }
  for (const failure of current.failed_steps) {
    const prev = previous.build.parts[failure.part_type]
    if (!prev) {
      updated.add(failure.part_type)
      continue
    }
    const hadFailure = previous.failed_steps.some((f) => f.part_type === failure.part_type)
    if (!hadFailure) {
      updated.add(failure.part_type)
    }
  }
  return updated
}

function friendlyChatError(error: unknown): string {
  const raw = error instanceof Error ? error.message.toLowerCase() : ''
  if (raw.includes('no compatible')) {
    return 'Horatix could not find a compatible selection for those constraints. Adjust one preference and try again.'
  }
  if (raw.includes('timeout')) {
    return 'The build took longer than expected. Your previous progress is safe; please retry.'
  }
  return 'The connection was interrupted before Horatix finished. Your previous build is safe; please retry.'
}

function App() {
  const posthog = usePostHog()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [debugUiEnabled, setDebugUiEnabled] = useState(false)
  const [catalogAdminUiEnabled, setCatalogAdminUiEnabled] = useState(false)
  const [messages, setMessages] = useState<UiChatMessage[]>(initialMessages)
  const [draft, setDraft] = useState('')
  const [conversationId, setConversationId] = useState<string | undefined>()
  const [buildResult, setBuildResult] = useState<WatchBuildResult | null>(null)
  const [, setPreviousBuildResult] = useState<WatchBuildResult | null>(null)
  const [updatedParts, setUpdatedParts] = useState<Set<string>>(new Set())
  const [isSending, setIsSending] = useState(false)
  const [isRestoring, setIsRestoring] = useState(() =>
    Boolean(
      resolveResumeConversationId(
        window.location.pathname,
        new URLSearchParams(window.location.search),
      ),
    ),
  )
  const [statusLabel, setStatusLabel] = useState<string | undefined>()
  const [buildProgress, setBuildProgress] = useState<BuildProgressSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [assembledImageUrl, setAssembledImageUrl] = useState<string | null>(null)
  const [isGeneratingImage, setIsGeneratingImage] = useState(false)
  const [imageError, setImageError] = useState<string | null>(null)
  const [imageLookupState, setImageLookupState] = useState<ImageLookupState>('idle')
  const [atelierTab, setAtelierTab] = useState<AtelierTab>('conversation')
  const [buildUnread, setBuildUnread] = useState(false)
  const clientBuildIdentityRef = useRef<string | null>(null)
  const imageOwnershipRef = useRef<{
    clientIdentity: string
    serverFingerprint: string
  } | null>(null)
  const lookupCompletedIdentityRef = useRef<string | null>(null)
  const lookupInFlightIdentityRef = useRef<string | null>(null)
  const cacheHitTrackedIdentityRef = useRef<string | null>(null)
  const streamAbortRef = useRef<AbortController | null>(null)
  const lastSubmittedMessageRef = useRef<string | null>(null)
  const studioStartedRef = useRef(false)
  const phaseTimingRef = useRef<{ phase: StudioPhase; startedAt: number } | null>(null)
  const restoreGenerationRef = useRef(0)
  const hydratedConversationIdRef = useRef<string | null>(null)
  const pendingPersistRef = useRef<WatchBuildResult | null>(null)
  const conversationQuery = searchParams.get(CONVERSATION_QUERY_PARAM)
  const currentBuildIdentity = buildResult ? buildIdentity(buildResult) : null
  const displayedImageUrl =
    assembledImageUrl &&
    currentBuildIdentity &&
    imageOwnershipRef.current?.clientIdentity === currentBuildIdentity
      ? assembledImageUrl
      : null

  const studioState = deriveStudioState({
    isSending,
    statusLabel,
    progress: buildProgress,
    buildResult,
    error,
    imageLookup: imageLookupState,
    isGeneratingImage,
  })

  const handleSubmit = async (messageOverride?: string) => {
    const message = (messageOverride ?? draft).trim()
    if (!message || isSending || isRestoring) return

    const userMessage: UiChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
    }

    setMessages((current) => [...current, userMessage])
    setDraft('')
    lastSubmittedMessageRef.current = message
    streamAbortRef.current?.abort()
    const abortController = new AbortController()
    streamAbortRef.current = abortController

    setIsSending(true)
    setError(null)
    setBuildUnread(false)
    setStatusLabel('Understanding your request…')
    setBuildProgress(null)
    if (!studioStartedRef.current) {
      studioStartedRef.current = true
      captureStudioStarted(posthog, buildResult !== null)
    } else if (buildResult) {
      captureStudioRefinementStarted(posthog, 'composer')
    }
    captureChatMessageSent(posthog, Boolean(conversationId))

    try {
      const response = await sendChatTurnStream(
        {
          message,
          conversation_id: conversationId,
        },
        {
          signal: abortController.signal,
          onEvent: (event) => {
            if (event.type === 'status' && event.label) {
              setStatusLabel(event.label)
            }
            if (event.type === 'build_progress' && event.progress) {
              setBuildProgress(event.progress)
              setStatusLabel(event.progress.label)
            }
          },
        },
      )

      setConversationId(response.conversation_id)
      hydratedConversationIdRef.current = response.conversation_id
      persistConversationId(response.conversation_id)
      registerConversation(posthog, response.conversation_id)
      captureChatTurnCompleted(posthog, response.build_result)

      if (response.build_result) {
        setPreviousBuildResult(buildResult)
        setBuildResult(response.build_result)
        setUpdatedParts(computeUpdatedParts(buildResult, response.build_result))
        setBuildUnread(true)
        if (isCompleteCompatibleBuild(response.build_result)) {
          captureStudioBuildReady(posthog, response.build_result)
        }
      }

      setMessages((current) => [
        ...current,
        {
          id: `assistant-${response.conversation_id}-${current.length}`,
          role: 'assistant',
          content: response.assistant_message,
          reviewBuild: response.build_result !== null,
        },
      ])
    } catch (caughtError) {
      if (caughtError instanceof Error && caughtError.name === 'AbortError') {
        return
      }
      if (caughtError instanceof Error) {
        Sentry.captureException(caughtError)
      }
      const rawMessage =
        caughtError instanceof Error ? caughtError.message : 'Unknown chat request failure'
      const displayMessage = friendlyChatError(caughtError)
      captureChatTurnFailed(posthog, rawMessage)
      setError(displayMessage)
      setMessages((current) => [
        ...current,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: displayMessage,
        },
      ])
    } finally {
      setIsSending(false)
      setStatusLabel(undefined)
      streamAbortRef.current = null
    }
  }

  const handleSuggestionClick = (suggestion: string, source: SuggestionSource) => {
    captureSuggestionClicked(posthog, source)
    if (source === 'hero_occasion') {
      void handleSubmit(suggestion)
      return
    }
    captureStudioRefinementStarted(posthog, 'suggestion')
    setAtelierTab('conversation')
    void handleSubmit(suggestion)
  }

  const handleStop = () => {
    if (!streamAbortRef.current) return
    const stoppedMessage = lastSubmittedMessageRef.current
    if (stoppedMessage) {
      setDraft((current) => current.trim() || stoppedMessage)
    }
    streamAbortRef.current.abort()
    setMessages((current) => [
      ...current,
      {
        id: `assistant-stopped-${Date.now()}`,
        role: 'assistant',
        content: buildResult
          ? 'Stopped. Your previous build is still available, and the stopped request is back in the composer to adjust or send again.'
          : 'Stopped. The request is back in the composer so you can adjust it or send it again.',
      },
    ])
  }

  const handleRetry = () => {
    const lastMessage = lastSubmittedMessageRef.current
    if (!lastMessage || isSending) return
    captureStudioRetryRequested(posthog)
    setError(null)
    setAtelierTab('conversation')
    void handleSubmit(lastMessage)
  }

  const handleTabChange = (tab: AtelierTab) => {
    const hadUnreadBadge = buildUnread
    setAtelierTab(tab)
    captureStudioViewChanged(posthog, tab)
    if (tab === 'build') {
      captureStudioBuildViewed(posthog, buildResult, hadUnreadBadge)
      setBuildUnread(false)
    }
  }

  const handleRefineBuild = () => {
    captureStudioRefinementStarted(posthog, 'build_action')
    setAtelierTab('conversation')
    setDraft((current) => current || 'Refine this build: ')
  }

  const handlePartPurchaseClick = (part: WatchPart, partType: string) => {
    const partCount = buildResult ? Object.keys(buildResult.build.parts).length : 0
    capturePurchaseLinkClicked(posthog, {
      click_type: 'part',
      source: 'part_card',
      part_id: part.id,
      provider_id: part.provider_id,
      part_type: partType,
      part_count_in_build: partCount,
      has_conversation_id: Boolean(conversationId),
    })
  }

  const handleBuyFullBuildClick = (parts: WatchPart[], total: number) => {
    captureBuyFullBuildClicked(posthog, {
      provider_id: parts[0]?.provider_id ?? 'unknown',
      part_count: parts.length,
      total_price: total,
      has_conversation_id: Boolean(conversationId),
    })
    capturePurchaseLinkClicked(posthog, {
      click_type: 'build_cart',
      source: 'buy_full_build',
      provider_id: parts[0]?.provider_id ?? 'unknown',
      part_count_in_build: parts.length,
      has_conversation_id: Boolean(conversationId),
    })
  }

  const handleCaseVariantChange = (variantId: string) => {
    setAssembledImageUrl(null)
    setImageError(null)
    setImageLookupState('idle')
    imageOwnershipRef.current = null
    lookupCompletedIdentityRef.current = null
    lookupInFlightIdentityRef.current = null
    cacheHitTrackedIdentityRef.current = null
    setBuildResult((current) => {
      if (!current?.build.parts.case) return current
      const updatedCase = applyCaseVariantSelection(current.build.parts.case, variantId)
      if (!updatedCase) return current
      const next = {
        ...current,
        build: {
          ...current.build,
          parts: {
            ...current.build.parts,
            case: updatedCase,
          },
        },
      }
      pendingPersistRef.current = next
      return next
    })
    setUpdatedParts((current) => new Set([...current, 'case']))
  }

  const handleStartNew = () => {
    restoreGenerationRef.current += 1
    hydratedConversationIdRef.current = null
    pendingPersistRef.current = null
    streamAbortRef.current?.abort()
    streamAbortRef.current = null
    clearStoredConversationId()
    if (searchParams.has(CONVERSATION_QUERY_PARAM)) {
      setSearchParams({}, { replace: true })
    }
    setConversationId(undefined)
    setBuildResult(null)
    setPreviousBuildResult(null)
    setUpdatedParts(new Set())
    setMessages(initialMessages)
    setDraft('')
    setError(null)
    setIsSending(false)
    setStatusLabel(undefined)
    setBuildProgress(null)
    setAssembledImageUrl(null)
    setIsGeneratingImage(false)
    setImageError(null)
    setImageLookupState('idle')
    setAtelierTab('conversation')
    setBuildUnread(false)
    setIsRestoring(false)
    studioStartedRef.current = false
    lastSubmittedMessageRef.current = null
    captureStudioNewBuildStarted(posthog)
  }

  const handleCopyLink = async () => {
    if (!conversationId || !buildResult) {
      throw new Error('Nothing to save yet')
    }
    const url = conversationPermalink(conversationId)
    await copyTextToClipboard(url)
    void saveConversationBuild(conversationId, buildResult)
      .then(() => captureStudioBuildLinkCopied(posthog, true))
      .catch(() => captureStudioBuildLinkCopied(posthog, false))
  }

  useEffect(() => {
    fetchHealth()
      .then((health) => {
        setDebugUiEnabled(health.debug_ui)
        setCatalogAdminUiEnabled(health.catalog_admin_ui)
      })
      .catch(() => {
        setDebugUiEnabled(false)
        setCatalogAdminUiEnabled(false)
      })
  }, [])

  useEffect(() => {
    const resumeId = resolveResumeConversationId(location.pathname, searchParams)
    if (!resumeId) {
      setIsRestoring(false)
      return
    }
    if (hydratedConversationIdRef.current === resumeId) {
      setIsRestoring(false)
      return
    }

    const generation = ++restoreGenerationRef.current
    const abort = new AbortController()
    const fromUrl =
      location.pathname === WATCH_BUILDER_PATH &&
      parseConversationId(conversationQuery) === resumeId

    setIsRestoring(true)

    void (async () => {
      try {
        const [conversation, messagesResponse] = await Promise.all([
          fetchConversation(resumeId),
          fetchConversationMessages(resumeId),
        ])
        if (abort.signal.aborted || generation !== restoreGenerationRef.current) return

        const restoredBuild = extractBuildResult(conversation.metadata)
        const restoredMessages = messagesFromConversation(messagesResponse.messages, {
          reviewLastAssistantBuild: restoredBuild !== null,
        })

        hydratedConversationIdRef.current = resumeId
        persistConversationId(resumeId)
        setConversationId(resumeId)
        registerConversation(posthog, resumeId)
        setBuildResult(restoredBuild)
        setPreviousBuildResult(null)
        setUpdatedParts(new Set())
        setMessages(restoredMessages.length > 0 ? restoredMessages : initialMessages)
        setError(null)
        setDraft('')
        setBuildProgress(null)
        setAssembledImageUrl(null)
        setImageError(null)
        setImageLookupState('idle')
        captureStudioBuildRestored(posthog, restoredBuild, fromUrl ? 'url' : 'local_storage')
      } catch (caughtError) {
        if (abort.signal.aborted || generation !== restoreGenerationRef.current) return
        hydratedConversationIdRef.current = null
        if (!fromUrl) {
          clearStoredConversationId()
        } else {
          const raw =
            caughtError instanceof Error ? caughtError.message.toLowerCase() : ''
          setError(
            raw.includes('not found')
              ? 'This saved build could not be found. Start a new one below.'
              : 'Could not restore that saved build. Start a new one below.',
          )
        }
        setConversationId(undefined)
        setBuildResult(null)
        setMessages(initialMessages)
      } finally {
        if (generation === restoreGenerationRef.current) {
          setIsRestoring(false)
        }
      }
    })()

    return () => abort.abort()
  }, [conversationQuery, location.pathname, posthog, searchParams])

  useEffect(() => {
    if (!conversationId) return
    persistConversationId(conversationId)
    if (location.pathname !== WATCH_BUILDER_PATH) return
    if (parseConversationId(conversationQuery) === conversationId) return
    setSearchParams({ [CONVERSATION_QUERY_PARAM]: conversationId }, { replace: true })
  }, [conversationId, conversationQuery, location.pathname, setSearchParams])

  useEffect(() => {
    if (!conversationId || !buildResult) return
    if (pendingPersistRef.current !== buildResult) return
    const snapshot = buildResult
    const handle = window.setTimeout(() => {
      if (pendingPersistRef.current !== snapshot) return
      pendingPersistRef.current = null
      void saveConversationBuild(conversationId, snapshot).catch(() => {
        // Auto-resume still has the last chat snapshot if this write fails.
      })
    }, 400)
    return () => window.clearTimeout(handle)
  }, [buildResult, conversationId])

  useEffect(() => {
    const now = performance.now()
    const previous = phaseTimingRef.current
    if (previous?.phase === studioState.phase) return
    if (previous && previous.phase !== 'drafting') {
      captureStudioPhaseCompleted(posthog, previous.phase, now - previous.startedAt)
    }
    phaseTimingRef.current = { phase: studioState.phase, startedAt: now }
  }, [posthog, studioState.phase])

  useEffect(() => {
    return () => streamAbortRef.current?.abort()
  }, [])

  useEffect(() => {
    if (!buildResult || !currentBuildIdentity) {
      setAssembledImageUrl(null)
      setImageError(null)
      setImageLookupState('idle')
      clientBuildIdentityRef.current = null
      imageOwnershipRef.current = null
      lookupCompletedIdentityRef.current = null
      lookupInFlightIdentityRef.current = null
      cacheHitTrackedIdentityRef.current = null
      return
    }

    if (
      clientBuildIdentityRef.current &&
      clientBuildIdentityRef.current !== currentBuildIdentity
    ) {
      setAssembledImageUrl(null)
      setImageError(null)
      setImageLookupState('idle')
      imageOwnershipRef.current = null
      lookupCompletedIdentityRef.current = null
      lookupInFlightIdentityRef.current = null
      cacheHitTrackedIdentityRef.current = null
    }
    clientBuildIdentityRef.current = currentBuildIdentity
  }, [buildResult, currentBuildIdentity])

  useEffect(() => {
    if (
      !buildResult ||
      !currentBuildIdentity ||
      isSending ||
      isGeneratingImage ||
      !isCompleteCompatibleBuild(buildResult)
    ) {
      if (buildResult && !isSending && !isCompleteCompatibleBuild(buildResult)) {
        setImageLookupState('idle')
      }
      return
    }

    if (
      assembledImageUrl &&
      imageOwnershipRef.current?.clientIdentity === currentBuildIdentity
    ) {
      setImageLookupState('found')
      return
    }
    if (
      lookupCompletedIdentityRef.current === currentBuildIdentity ||
      lookupInFlightIdentityRef.current === currentBuildIdentity
    ) {
      return
    }

    const lookupIdentity = currentBuildIdentity
    const controller = new AbortController()
    lookupInFlightIdentityRef.current = lookupIdentity
    setImageLookupState('looking')

    void lookupAssembledImage(buildResult, { signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) {
          return
        }
        if (clientBuildIdentityRef.current !== lookupIdentity) {
          return
        }
        lookupInFlightIdentityRef.current = null
        lookupCompletedIdentityRef.current = lookupIdentity
        if (result === null) {
          setImageLookupState('missing')
          return
        }
        imageOwnershipRef.current = {
          clientIdentity: lookupIdentity,
          serverFingerprint: result.build_fingerprint,
        }
        setAssembledImageUrl(result.image_url)
        setImageLookupState('found')
        if (cacheHitTrackedIdentityRef.current !== lookupIdentity) {
          cacheHitTrackedIdentityRef.current = lookupIdentity
          captureAssembledImageCacheHit(posthog, result.build_fingerprint)
        }
      })
      .catch((caughtError) => {
        if (controller.signal.aborted) {
          return
        }
        if (caughtError instanceof Error && caughtError.name === 'AbortError') {
          return
        }
        lookupInFlightIdentityRef.current = null
        lookupCompletedIdentityRef.current = lookupIdentity
        if (clientBuildIdentityRef.current === lookupIdentity) {
          setImageLookupState('unavailable')
        }
      })

    return () => {
      controller.abort()
      if (lookupInFlightIdentityRef.current === lookupIdentity) {
        lookupInFlightIdentityRef.current = null
      }
    }
  }, [
    assembledImageUrl,
    buildResult,
    currentBuildIdentity,
    isGeneratingImage,
    isSending,
    posthog,
  ])

  const handleGenerateImage = async () => {
    if (!buildResult || isGeneratingImage || isSending) return

    const generationIdentity = buildIdentity(buildResult)
    setIsGeneratingImage(true)
    setImageError(null)
    const partCount = Object.keys(buildResult.build.parts).length
    captureAssembledImageRequested(posthog, partCount)

    try {
      const result = await generateAssembledImage(buildResult)
      if (clientBuildIdentityRef.current !== generationIdentity) {
        return
      }
      imageOwnershipRef.current = {
        clientIdentity: generationIdentity,
        serverFingerprint: result.build_fingerprint,
      }
      lookupCompletedIdentityRef.current = generationIdentity
      lookupInFlightIdentityRef.current = null
      setAssembledImageUrl(result.image_url)
      setImageLookupState('found')
      captureAssembledImageCompleted(posthog, result.cached)
      if (result.cached && cacheHitTrackedIdentityRef.current !== generationIdentity) {
        cacheHitTrackedIdentityRef.current = generationIdentity
        captureAssembledImageCacheHit(posthog, result.build_fingerprint)
      }
    } catch (caughtError) {
      if (clientBuildIdentityRef.current !== generationIdentity) {
        return
      }
      if (caughtError instanceof Error) {
        Sentry.captureException(caughtError)
      }
      const message =
        caughtError instanceof Error
          ? caughtError.message
          : 'Could not generate the assembled watch image.'
      captureAssembledImageFailed(posthog, message)
      setImageError(message)
    } finally {
      setIsGeneratingImage(false)
    }
  }

  return (
    <div
      className={`app-shell-root${debugUiEnabled || catalogAdminUiEnabled ? ' debug-mode' : ''}`}
    >
      {debugUiEnabled || catalogAdminUiEnabled ? (
        <nav className="global-nav">
          <div className="nav-logo">
            <strong>Horatix</strong> <span className="logo-badge">ENGINE v2</span>
          </div>
          <div className="nav-links">
            <NavLink
              to="/watch-builder"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              Watch Builder
            </NavLink>
            {debugUiEnabled ? (
              <>
                <NavLink
                  to="/debug/retrieval"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  Retrieval Lab
                </NavLink>
                <NavLink
                  to="/debug/agent"
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                >
                  Agent Observer
                </NavLink>
              </>
            ) : null}
            {catalogAdminUiEnabled ? (
              <NavLink
                to="/admin/catalog"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                Catalog
              </NavLink>
            ) : null}
          </div>
        </nav>
      ) : null}

      <Routes>
        <Route
          path="/"
          element={
            <SiteLayout>
              <HomePage />
            </SiteLayout>
          }
        />
        <Route
          path="/watch-builder"
          element={
            <SiteLayout fixedHeight compactHeader>
              <AtelierShell
                activeTab={atelierTab}
                onTabChange={handleTabChange}
                isWorking={studioState.isWorking}
                buildUnread={buildUnread}
                chat={
                  <ChatPanel
                    messages={messages}
                    draft={draft}
                    isSending={isSending}
                    loadingLabel={statusLabel}
                    buildProgress={buildProgress}
                    error={error}
                    suggestions={milestoneQuotes}
                    onSuggestionClick={(text) => handleSuggestionClick(text, 'hero_occasion')}
                    onDraftChange={setDraft}
                    onSubmit={handleSubmit}
                    onStop={handleStop}
                    onRetry={lastSubmittedMessageRef.current ? handleRetry : undefined}
                    onReviewBuild={() => handleTabChange('build')}
                    isRestoring={isRestoring}
                    onStartNew={
                      conversationId ||
                      buildResult ||
                      messages.some((message) => message.role === 'user')
                        ? handleStartNew
                        : undefined
                    }
                  />
                }
                stage={
                  <WatchStage
                    buildResult={buildResult}
                    isSending={isSending}
                    loadingLabel={statusLabel}
                    buildProgress={buildProgress}
                    assembledImageUrl={displayedImageUrl}
                    isGeneratingImage={isGeneratingImage}
                    imageError={imageError}
                    onGenerateImage={handleGenerateImage}
                    studioState={studioState}
                    imageLookupState={imageLookupState}
                  />
                }
                loupe={
                  <TechnicalLoupe
                    buildResult={buildResult}
                    buildProgress={buildProgress}
                    isSending={isSending}
                    updatedParts={updatedParts}
                    conversationId={conversationId}
                    onSuggestionClick={(suggestion) =>
                      handleSuggestionClick(suggestion, 'build_summary')
                    }
                    onPartPurchaseClick={handlePartPurchaseClick}
                    onBuyFullBuildClick={handleBuyFullBuildClick}
                    onCaseVariantChange={handleCaseVariantChange}
                    onRefine={handleRefineBuild}
                    isRestoring={isRestoring}
                    onCopyLink={conversationId ? handleCopyLink : undefined}
                  />
                }
              />
            </SiteLayout>
          }
        />
        {debugUiEnabled ? (
          <>
            <Route path="/debug/retrieval" element={<SiteLayout><RetrievalLab /></SiteLayout>} />
            <Route path="/debug/agent" element={<SiteLayout><AgentObserver /></SiteLayout>} />
          </>
        ) : (
          <Route path="/debug/*" element={<Navigate to="/" replace />} />
        )}
        {catalogAdminUiEnabled ? (
          <Route path="/admin/catalog" element={<CatalogAdminLayout />}>
            <Route index element={<CatalogAdmin />} />
            <Route path=":partId" element={<CatalogPartEditor />} />
          </Route>
        ) : (
          <Route path="/admin/*" element={<Navigate to="/" replace />} />
        )}
      </Routes>
    </div>
  )
}

export default App
